import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useLanguage from '../../hooks/useLanguage'

const CELL_SIZE = 14
const CELL_GAP = 3
const LABEL_WIDTH = 32

const ActivityHeatmap = ({ data = [], weeks = 18 }) => {
  const [hoveredCell, setHoveredCell] = useState(null)
  const { locale } = useLanguage()
  const { t } = useTranslation('common')

  const DAYS = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' })
    // Generate Mon-Sun labels (ISO week starts Monday)
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(2024, 0, i + 1) // 2024-01-01 is a Monday
      return formatter.format(d)
    })
  }, [locale])

  const { grid, maxAmount, monthLabels } = useMemo(() => {
    const dataMap = {}
    let max = 0

    data.forEach((item) => {
      dataMap[item.date] = item
      if (item.amount > max) max = item.amount
    })

    const today = new Date()
    const totalDays = weeks * 7
    const startDate = new Date(today)
    startDate.setDate(startDate.getDate() - totalDays + 1)

    // Align to Monday
    const dayOfWeek = startDate.getDay()
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
    startDate.setDate(startDate.getDate() + mondayOffset)

    const cells = []
    const months = []
    let lastMonth = -1

    for (let i = 0; i < totalDays + 7; i++) {
      const date = new Date(startDate)
      date.setDate(date.getDate() + i)

      if (date > today) break

      const dateStr = date.toISOString().split('T')[0]
      const weekIndex = Math.floor(i / 7)
      const dayIndex = i % 7

      const month = date.getMonth()
      if (month !== lastMonth) {
        months.push({
          label: date.toLocaleDateString(locale, { month: 'short' }),
          weekIndex,
        })
        lastMonth = month
      }

      cells.push({
        date: dateStr,
        weekIndex,
        dayIndex,
        amount: dataMap[dateStr]?.amount || 0,
        count: dataMap[dateStr]?.count || 0,
        displayDate: date.toLocaleDateString(locale, { day: 'numeric', month: 'short' }),
      })
    }

    return { grid: cells, maxAmount: max || 1, monthLabels: months }
  }, [data, weeks, locale])

  const getColor = (amount) => {
    if (amount === 0) return 'var(--color-heatmap-empty)'
    const intensity = Math.min(amount / maxAmount, 1)

    if (intensity < 0.25) return 'rgba(31,159,147,0.25)'
    if (intensity < 0.5) return 'rgba(31,159,147,0.50)'
    if (intensity < 0.75) return 'rgba(31,159,147,0.75)'
    return '#1f9f93'
  }

  const totalWeeks = grid.length > 0 ? grid[grid.length - 1].weekIndex + 1 : 0
  const svgWidth = LABEL_WIDTH + totalWeeks * (CELL_SIZE + CELL_GAP)
  const svgHeight = 20 + 7 * (CELL_SIZE + CELL_GAP)

  return (
    <div className="relative">
      <svg width="100%" viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="overflow-visible">
        {/* Month labels */}
        {monthLabels.map((m, i) => (
          <text
            key={i}
            x={LABEL_WIDTH + m.weekIndex * (CELL_SIZE + CELL_GAP)}
            y={10}
            fill="var(--color-text-faint)"
            fontSize="9"
          >
            {m.label}
          </text>
        ))}

        {/* Day labels */}
        {[1, 3, 5].map((dayIdx) => (
          <text
            key={dayIdx}
            x={0}
            y={20 + dayIdx * (CELL_SIZE + CELL_GAP) + CELL_SIZE - 2}
            fill="var(--color-text-faint)"
            fontSize="9"
          >
            {DAYS[dayIdx]}
          </text>
        ))}

        {/* Cells */}
        {grid.map((cell, i) => (
          <rect
            key={i}
            x={LABEL_WIDTH + cell.weekIndex * (CELL_SIZE + CELL_GAP)}
            y={20 + cell.dayIndex * (CELL_SIZE + CELL_GAP)}
            width={CELL_SIZE}
            height={CELL_SIZE}
            rx={2}
            fill={getColor(cell.amount)}
            className="transition-opacity cursor-pointer"
            opacity={hoveredCell && hoveredCell !== cell.date ? 0.6 : 1}
            onMouseEnter={() => setHoveredCell(cell.date)}
            onMouseLeave={() => setHoveredCell(null)}
          />
        ))}
      </svg>

      {/* Tooltip */}
      {hoveredCell && (() => {
        const cell = grid.find((c) => c.date === hoveredCell)
        if (!cell) return null
        return (
          <div
            className="absolute z-10 px-3 py-2 text-xs rounded-lg shadow-lg pointer-events-none"
            style={{
              left: LABEL_WIDTH + cell.weekIndex * (CELL_SIZE + CELL_GAP),
              top: 20 + cell.dayIndex * (CELL_SIZE + CELL_GAP) - 44,
              background: 'var(--color-tooltip-bg)',
              backdropFilter: 'blur(8px)',
              border: '1px solid var(--color-border)',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{ color: 'var(--color-text)', fontWeight: 500 }}>
              {cell.amount > 0 ? `${cell.amount.toFixed(0)} ${t('currency')}` : t('noData')}
            </span>
            <span style={{ color: 'var(--color-text-muted)', marginLeft: '4px' }}>
              {cell.count > 0 ? `(${cell.count} trans.)` : ''} · {cell.displayDate}
            </span>
          </div>
        )
      })()}

      {/* Legend */}
      <div className="flex items-center gap-1.5 mt-2 text-xs" style={{ color: 'var(--color-text-faint)' }}>
        <span>0</span>
        {[0, 0.25, 0.5, 0.75, 1].map((intensity) => (
          <div
            key={intensity}
            className="rounded-sm"
            style={{
              width: 10,
              height: 10,
              backgroundColor: getColor(intensity === 0 ? 0 : maxAmount * intensity),
            }}
          />
        ))}
        <span>{maxAmount.toFixed(0)} {t('currency')}</span>
      </div>
    </div>
  )
}

export default ActivityHeatmap
